import { Dispatch, SetStateAction, useState } from 'react';
import useApiRequest from '../../../../lib/hooks/useRequest';
import { AssignedInternalUserResponseDto, OrganizationDetailsResponseDto } from '../../../../lib/interfaces/organisation';
import { H4 } from '../../../../ui/components/ui/headings';
import { Switch } from '../../../../ui/components/ui/switch';
import AssignmentMembersEditor from './assignment-members-editor';
import AssignmentSingleForm from './assignment-single-form';

export interface AssignmentCardProps {
  organisation: OrganizationDetailsResponseDto;
  organisationId: string;
  setReload: Dispatch<SetStateAction<boolean>>;
}

/**
 * Single place to manage who takes care of a customer.
 *
 * Switch off - the unchanged single assignment plus representative.
 * Switch on  - several internal users, each with his share of the allowedJobsCount.
 *              A member with share 0 is the multiple assignment equivalent of the representative.
 */
const AssignmentCard = ({ organisation, organisationId, setReload }: AssignmentCardProps) => {
  const { apiRequest } = useApiRequest();
  const [isSaving, setIsSaving] = useState(false);

  const assignedMembers = organisation.assignedInternalUsers ?? [];
  // Remounts the editor whenever the server state changes, which resets its draft without an effect.
  const membersKey = `${assignedMembers.map((member) => `${member.belongsToUserId}:${member.capacity}`).join('_')}`;

  const toggleMultipleAssignment = async (enabled: boolean) => {
    setIsSaving(true);
    await apiRequest(`organizations/internal/${organisationId}/multipleAssignment`, 'PATCH', {
      body: { enabled },
      toast: { toastText: enabled ? 'Mehrfach-Zuweisung aktiviert' : 'Mehrfach-Zuweisung deaktiviert' }
    });
    setIsSaving(false);
    setReload((previous) => !previous);
  };

  const saveMembers = async (members: AssignedInternalUserResponseDto[]) => {
    setIsSaving(true);
    await apiRequest(`organizations/internal/${organisationId}/assignedInternalUsers`, 'PATCH', {
      body: {
        assignedInternalUsers: members.map((member) => ({ belongsToUserId: member.belongsToUserId, capacity: member.capacity }))
      },
      toast: { toastText: 'Zuweisung gespeichert' }
    });
    setIsSaving(false);
    setReload((previous) => !previous);
  };

  const changeAssignedInternalUser = async (belongsToUserId: string) => {
    setIsSaving(true);
    await apiRequest(`organizations/internal/${organisationId}/assignedInternalUser`, 'PATCH', {
      body: { assignedInternalUserId: belongsToUserId === 'NONE' ? undefined : belongsToUserId },
      toast: { toastText: 'Ansprechpartner geändert' }
    });
    setIsSaving(false);
    setReload((previous) => !previous);
  };

  const changeAssignedRepresentativeUser = async (belongsToUserId: string) => {
    setIsSaving(true);
    await apiRequest(`organizations/internal/${organisationId}/assignedRepresentativeUser`, 'PATCH', {
      body: { assignedRepresentativeUserId: belongsToUserId === 'NONE' ? undefined : belongsToUserId },
      toast: { toastText: 'Vertreter geändert' }
    });
    setIsSaving(false);
    setReload((previous) => !previous);
  };

  return (
    <div className="flex flex-col gap-4 rounded-lg bg-white px-9 py-7">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <H4>Zuweisung</H4>
          <p className="mt-[2px] text-[13px] text-[#888]">Wer betreut diesen Kunden?</p>
        </div>
        <div className="flex items-center gap-[11px]">
          <span className="text-[14px] font-semibold text-[#12282A]">Mehrere Mitarbeiter</span>
          <Switch
            checked={organisation.multipleAssignmentEnabled}
            disabled={isSaving}
            onCheckedChange={toggleMultipleAssignment}
            aria-label="Mehrere Mitarbeiter zuweisen"
          />
        </div>
      </div>

      {organisation.multipleAssignmentEnabled ? (
        <AssignmentMembersEditor
          key={membersKey}
          allowedJobsCount={organisation.allowedJobsCount}
          initialMembers={assignedMembers}
          selectableUsers={organisation.selectableEntities.internalUsers}
          isSaving={isSaving}
          onSave={saveMembers}
        />
      ) : (
        <AssignmentSingleForm
          organisation={organisation}
          isSaving={isSaving}
          onAssignedInternalUserChange={changeAssignedInternalUser}
          onAssignedRepresentativeUserChange={changeAssignedRepresentativeUser}
        />
      )}
    </div>
  );
};

export default AssignmentCard;
